// Phase 1.D Component Library — Production Demo Page
//
// מציג בשילוב את כל 11 הקומפוננטות שנבנו ב-Phase 1.D.1/2/3.
// route: /profitability/demo (תחת ProfitabilityPage shell)
//
// **מטרה:** showcase מלא של ה-Component Library בייצור — מעבר ל-Storybook.

import { useEffect, useState, ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  ProfitabilityLayout,
  PageHeader,
  MoneyDisplay,
  SummaryCard,
  MonthYearSelector,
  CardRadioGroup,
  DrillDownDrawer,
  FilePreview,
  FileUploadField,
  TerminalMultiSelect,
  LinkedMonthsRange,
} from '../../components/Profitability';
import type { Terminal } from '../../components/Profitability/primitives/TerminalMultiSelect/TerminalMultiSelect';
import type { MonthYear } from '../../components/Profitability/primitives/LinkedMonthsRange/LinkedMonthsRange';

type SetTitle = (title: string) => void;
type CrumbItem = { title: string; link: string };
type SetCrumb = (items: CrumbItem[]) => void;
type OutletContext = [SetTitle, SetCrumb];

const SAMPLE_TERMINALS: Terminal[] = [
  { id: 1, name: 'מסוף ראשי - יד אליהו', code: 'TRM-001', location: 'יד אליהו, תל אביב', active: true, groupId: 10 },
  { id: 2, name: 'מסוף משני - גרנד', code: 'TRM-002', location: 'גרנד מאלל, חיפה', active: true, groupId: 10 },
  { id: 3, name: 'מסוף שלישי - הרצליה', code: 'TRM-003', location: 'הרצליה פיתוח', active: false, groupId: 20, assignedTo: 'קבוצה א' },
  { id: 4, name: 'מסוף רביעי - באר שבע', code: 'TRM-004', location: 'באר שבע מרכז', active: true, groupId: 20 },
  { id: 5, name: 'מסוף חמישי - אילת', code: 'TRM-005', location: 'אילת מלון', active: true, groupId: 30, assignedTo: 'קבוצה ב' },
  { id: 6, name: 'מסוף שישי - אשדוד', code: 'TRM-006', location: 'אשדוד נמל', active: true, groupId: 30 },
];

const PRIMARY_BTN: React.CSSProperties = {
  padding: 'var(--profit-space-2) var(--profit-space-4)',
  background: 'var(--profit-emerald)',
  color: 'var(--profit-surface)',
  border: 'none',
  borderRadius: 'var(--profit-radius)',
  cursor: 'pointer',
  fontWeight: 700,
  fontFamily: 'var(--profit-font-body)',
  fontSize: 'var(--profit-text-md)',
};

const SECONDARY_BTN: React.CSSProperties = {
  ...PRIMARY_BTN,
  background: 'var(--profit-surface)',
  color: 'var(--profit-ink-2)',
  border: '1px solid var(--profit-line-2)',
  fontWeight: 500,
};

const SECTION_STYLE: React.CSSProperties = {
  padding: 'var(--profit-space-7) var(--profit-space-9)',
  borderBottom: '1px solid var(--profit-line)',
};

const SECTION_HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--profit-space-3)',
  marginBottom: 'var(--profit-space-5)',
};

const SECTION_TITLE: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--profit-text-xl)',
  fontWeight: 600,
  color: 'var(--profit-ink)',
  fontFamily: 'var(--profit-font-display)',
};

const SECTION_SUB: React.CSSProperties = {
  fontSize: 'var(--profit-text-sm)',
  color: 'var(--profit-muted)',
};

const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 'var(--profit-space-4)',
};

const ProfitabilityDemo = (): JSX.Element => {
  // Resilient to both contexts:
  // - mounted under ProfitabilityPage shell → outlet context is [setTitle, setCrumb]
  // - mounted as standalone public route → outlet context is {} (no-op)
  const ctx = useOutletContext<OutletContext | object>();

  useEffect(() => {
    if (!Array.isArray(ctx)) return;
    const [setPageTitle, setCrumbItem] = ctx;
    setPageTitle('Component Library — Production Demo');
    setCrumbItem([
      { title: 'רווחיות', link: '#' },
      { title: 'Component Demo', link: '#' },
    ]);
  }, [ctx]);

  const now = new Date();
  const [date, setDate] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [paymentMode, setPaymentMode] = useState<'single' | 'recurring' | 'spread'>('single');
  const [drill, setDrill] = useState<{ open: boolean; title: string; content?: ReactNode }>({
    open: false,
    title: '',
  });
  const [invoiceFile, setInvoiceFile] = useState<File | string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedTerminals, setSelectedTerminals] = useState<Set<number>>(new Set([1, 3]));
  const [linkedMonths, setLinkedMonths] = useState<MonthYear[]>([
    { month: 4, year: 2026 },
    { month: 5, year: 2026 },
  ]);

  const openDrill = (title: string, content: ReactNode): void => {
    setDrill({ open: true, title, content });
  };

  const closeDrill = (): void => {
    setDrill((d) => ({ ...d, open: false }));
  };

  // Build revenue detail (sample) per metric
  const revenueDetailContent: ReactNode = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--profit-space-4)' }}>
      <div>
        <div style={{ color: 'var(--profit-muted)', fontSize: 'var(--profit-text-sm)' }}>סך הכנסות:</div>
        <MoneyDisplay amount={89500} variant="large" emphasis="positive" trend="up" trendValue={12.5} />
      </div>
      <div style={{ borderTop: '1px solid var(--profit-line)', paddingTop: 'var(--profit-space-4)' }}>
        <strong>פירוט לפי מקור:</strong>
        <ul style={{ paddingInlineStart: 'var(--profit-space-5)', marginTop: 'var(--profit-space-2)' }}>
          <li>שירות מסופים — <MoneyDisplay amount={65000} variant="mono" /></li>
          <li>שכירות — <MoneyDisplay amount={15000} variant="mono" /></li>
          <li>אחר — <MoneyDisplay amount={9500} variant="mono" /></li>
        </ul>
      </div>
    </div>
  );

  return (
    <ProfitabilityLayout>
      <PageHeader
        eyebrow="◆ Phase 1.D Library"
        title="Profitability Components — Production Demo"
        subtitle="11 קומפוננטות + 79 stories + 786 tests + 94.91% coverage"
        breadcrumbs={[
          { label: 'בית', href: '/dashboard' },
          { label: 'רווחיות', href: '/profitability' },
          { label: 'Demo' },
        ]}
        filterSlot={
          <MonthYearSelector
            value={date}
            onChange={setDate}
            variant="compact"
            showCurrentBadge
          />
        }
        actions={
          <>
            <button type="button" style={SECONDARY_BTN}>ייצוא Excel</button>
            <button type="button" style={PRIMARY_BTN}>+ הוסף הוצאה</button>
          </>
        }
      />

      {/* ─── Section 1: Summary Cards (R3 — drill-down) ───────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>1. Summary Cards</h2>
          <span style={SECTION_SUB}>
            4 metrics · clickable drill-down · status accent · trend
          </span>
        </header>
        <div style={GRID}>
          <SummaryCard
            title="הכנסות חודש"
            value={89500}
            status="success"
            trend={{ value: 12.5, period: 'מהחודש שעבר' }}
            subtitle="כולל מע״מ"
            onClick={() => openDrill('פירוט הכנסות מאי 2026', revenueDetailContent)}
            drillDownLabel="פתח פירוט הכנסות"
          />
          <SummaryCard
            title="הוצאות"
            value={56750}
            status="warning"
            trend={{ value: 3.2, period: 'מהחודש שעבר' }}
            subtitle="קרוב לסף"
            onClick={() =>
              openDrill(
                'פירוט הוצאות מאי 2026',
                <p>פירוט הוצאות — לדוגמה. תוכן אמיתי מגיע מ-API בייצור.</p>
              )
            }
          />
          <SummaryCard
            title="רווח נקי"
            value={32750.5}
            status="success"
            trend={{ value: 24.8, period: 'מהחודש שעבר' }}
            onClick={() =>
              openDrill(
                'פירוט רווח נקי',
                <p>הכנסות − הוצאות = רווח. מומלץ להציג טבלה כאן בייצור.</p>
              )
            }
          />
          <SummaryCard
            title="מסופים פעילים"
            value="567"
            subtitle="מתוך 580 רשומים"
            status="info"
          />
        </div>
      </section>

      {/* ─── Section 2: SummaryCard states ─────────────────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>2. SummaryCard — States</h2>
          <span style={SECTION_SUB}>loading skeleton · error · status variants</span>
        </header>
        <div style={GRID}>
          <SummaryCard title="טוען" value={0} loading />
          <SummaryCard
            title="הוצאות"
            value={0}
            error="כשל בטעינת הנתונים מהשרת"
            status="danger"
          />
          <SummaryCard title="ניטרלי" value={50000} status="neutral" subtitle="ללא דגש" />
          <SummaryCard title="סכנה" value={15000} status="danger" subtitle="מתחת ליעד" />
        </div>
      </section>

      {/* ─── Section 3: MoneyDisplay variants ──────────────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>3. MoneyDisplay</h2>
          <span style={SECTION_SUB}>
            4 variants · 3 emphasis colors · 3 trend states · 3 currencies · null state
          </span>
        </header>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--profit-space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--profit-space-5)', flexWrap: 'wrap' }}>
            <MoneyDisplay amount={1234.56} />
            <MoneyDisplay amount={-500.25} emphasis="negative" />
            <MoneyDisplay amount={5000} emphasis="positive" trend="up" trendValue={12.5} />
            <MoneyDisplay amount={null} />
          </div>
          <div style={{ display: 'flex', gap: 'var(--profit-space-5)', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <MoneyDisplay amount={9999} variant="large" emphasis="positive" />
            <MoneyDisplay amount={1234.56} variant="mono" />
            <MoneyDisplay amount={89.5} variant="small" />
          </div>
          <div style={{ display: 'flex', gap: 'var(--profit-space-5)' }}>
            <MoneyDisplay amount={1234.56} currency="ILS" />
            <MoneyDisplay amount={1234.56} currency="USD" />
            <MoneyDisplay amount={1234.56} currency="EUR" />
          </div>
        </div>
      </section>

      {/* ─── Section 4: CardRadioGroup (R4) ─────────────────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>4. CardRadioGroup</h2>
          <span style={SECTION_SUB}>
            ARIA radiogroup · roving tabIndex · arrow keys + Home/End · 3 layouts
          </span>
        </header>
        <CardRadioGroup
          options={[
            {
              value: 'single' as const,
              label: 'תשלום בודד',
              description: 'חיוב חד-פעמי בחודש זה',
              icon: '◇',
              badge: 'ברירת מחדל',
            },
            {
              value: 'recurring' as const,
              label: 'תשלום חוזר',
              description: 'חיוב חודשי קבוע',
              icon: '↻',
            },
            {
              value: 'spread' as const,
              label: 'תשלום פרוס',
              description: 'חלוקה ל-מספר חודשים רצופים',
              icon: '⇉',
              badge: 'מומלץ',
            },
          ]}
          value={paymentMode}
          onChange={setPaymentMode}
          ariaLabel="אופן תשלום"
        />
      </section>

      {/* ─── Section 5: LinkedMonthsRange (visible only when spread) ───── */}
      {paymentMode === 'spread' && (
        <section style={SECTION_STYLE}>
          <header style={SECTION_HEADER}>
            <h2 style={SECTION_TITLE}>5. LinkedMonthsRange (R5)</h2>
            <span style={SECTION_SUB}>
              consecutive validation · min/max enforcement · year boundary handling
            </span>
          </header>
          <LinkedMonthsRange
            value={linkedMonths}
            onChange={setLinkedMonths}
            minMonths={2}
            maxMonths={6}
            requireConsecutive
          />
        </section>
      )}

      {/* ─── Section 6: FileUploadField + FilePreview ──────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>6. FileUploadField + FilePreview</h2>
          <span style={SECTION_SUB}>
            DnD · click-to-pick · MIME + size validation · composition עם FilePreview
          </span>
        </header>
        <div style={{ maxWidth: 480 }}>
          <FileUploadField
            value={invoiceFile}
            onChange={(f) => {
              setInvoiceFile(f);
              setFileError(null);
            }}
            label="חשבונית הוצאה"
            required
            accept="image/*,.pdf"
            maxSizeBytes={10 * 1024 * 1024}
            onError={setFileError}
          />
          {fileError && (
            <div
              role="alert"
              style={{
                marginTop: 'var(--profit-space-2)',
                color: 'var(--profit-crimson)',
                fontSize: 'var(--profit-text-sm)',
                fontWeight: 500,
              }}
            >
              ⚠ {fileError}
            </div>
          )}
        </div>
      </section>

      {/* ─── Section 7: TerminalMultiSelect ─────────────────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>7. TerminalMultiSelect</h2>
          <span style={SECTION_SUB}>
            search · bulk actions · group filter · assigned badges
          </span>
        </header>
        <div style={{ maxWidth: 720 }}>
          <TerminalMultiSelect
            availableTerminals={SAMPLE_TERMINALS}
            selectedIds={selectedTerminals}
            onChange={setSelectedTerminals}
            ariaLabel="בחירת מסופים להקצאה"
            showAssignedBadge
          />
        </div>
      </section>

      {/* ─── Section 8: FilePreview standalone ──────────────────────────── */}
      <section style={SECTION_STYLE}>
        <header style={SECTION_HEADER}>
          <h2 style={SECTION_TITLE}>8. FilePreview (R9 actions)</h2>
          <span style={SECTION_SUB}>
            view · download · edit · delete · thumbnail/inline variants
          </span>
        </header>
        <div style={{ display: 'flex', gap: 'var(--profit-space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FilePreview
            fileUrl="https://picsum.photos/seed/demo1/400/300.jpg"
            fileName="חשבונית-1.jpg"
            onView={() => alert('צפייה ב-חשבונית-1')}
            onDownload={() => alert('הורדה')}
            onDelete={() => alert('מחיקה')}
          />
          <FilePreview
            fileUrl="https://example.com/invoice.pdf"
            fileName="חשבונית-2.pdf"
            onView={() => alert('צפייה ב-חשבונית-2')}
            onDownload={() => alert('הורדה')}
          />
          <FilePreview
            fileUrl="https://example.com/document.xyz"
            fileName="קובץ-לא-מזוהה.xyz"
            onView={() => alert('צפייה')}
          />
        </div>
      </section>

      {/* ─── DrillDownDrawer (controlled globally) ─────────────────────── */}
      <DrillDownDrawer
        isOpen={drill.open}
        onClose={closeDrill}
        title={drill.title}
        size="lg"
      >
        {drill.content}
      </DrillDownDrawer>
    </ProfitabilityLayout>
  );
};

export default ProfitabilityDemo;
